import React, { useState } from 'react';
import { 
  Zap, 
  Activity, 
  Code, 
  Server, 
  AlertTriangle,
  CheckCircle,
  Clock,
  TrendingUp,
  TrendingDown,
  Database,
  Shield,
  Cpu,
  HardDrive,
  Network,
  GitBranch,
  Users,
  Target
} from 'lucide-react';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, AreaChart, Area, BarChart, Bar, PieChart, Pie, Cell } from 'recharts';

const TechnologyDashboard: React.FC = () => {
  const [selectedTimeframe, setSelectedTimeframe] = useState('24h');
  const [selectedService, setSelectedService] = useState('all');

  // System health data
  const systemHealthData = [
    { time: '00:00', uptime: 99.9, response: 120, errors: 2, throughput: 1200 },
    { time: '04:00', uptime: 99.8, response: 135, errors: 5, throughput: 800 },
    { time: '08:00', uptime: 99.9, response: 110, errors: 1, throughput: 2400 },
    { time: '12:00', uptime: 99.7, response: 180, errors: 8, throughput: 3200 },
    { time: '16:00', uptime: 99.9, response: 95, errors: 0, throughput: 2800 },
    { time: '20:00', uptime: 99.8, response: 125, errors: 3, throughput: 1800 },
  ];

  const deploymentData = [
    { week: 'W1', deployments: 12, success: 11, rollbacks: 1, leadTime: 2.5 },
    { week: 'W2', deployments: 15, success: 14, rollbacks: 1, leadTime: 2.1 },
    { week: 'W3', deployments: 18, success: 17, rollbacks: 1, leadTime: 1.8 },
    { week: 'W4', deployments: 22, success: 21, rollbacks: 1, leadTime: 1.5 },
    { week: 'W5', deployments: 25, success: 24, rollbacks: 1, leadTime: 1.2 },
    { week: 'W6', deployments: 28, success: 27, rollbacks: 1, leadTime: 1.0 },
  ];

  const serviceHealthData = [
    { service: 'API Gateway', status: 'healthy', uptime: 99.9, response: 85, load: 65 },
    { service: 'User Service', status: 'healthy', uptime: 99.8, response: 120, load: 72 },
    { service: 'Payment Service', status: 'warning', uptime: 99.5, response: 250, load: 88 },
    { service: 'Analytics Service', status: 'healthy', uptime: 99.9, response: 95, load: 45 },
    { service: 'Notification Service', status: 'critical', uptime: 98.2, response: 450, load: 95 },
  ];

  const technicalDebtData = [
    { category: 'Code Quality', debt: 25, color: '#EF4444' },
    { category: 'Security', debt: 15, color: '#F59E0B' },
    { category: 'Performance', debt: 30, color: '#8B5CF6' },
    { category: 'Documentation', debt: 20, color: '#3B82F6' },
    { category: 'Testing', debt: 10, color: '#10B981' },
  ];

  // Key technology metrics
  const techMetrics = [
    {
      id: 'system_uptime',
      name: 'System Uptime',
      value: 99.8,
      change: 0.1,
      positive: true,
      description: 'Overall system availability',
      target: 99.9,
      unit: '%',
      status: 'healthy'
    },
    {
      id: 'deployment_frequency',
      name: 'Deployment Frequency',
      value: 28,
      change: 12.0,
      positive: true,
      description: 'Deployments per week',
      target: 25,
      unit: '/week',
      status: 'healthy'
    },
    {
      id: 'lead_time',
      name: 'Lead Time',
      value: 1.0,
      change: -58.3,
      positive: true,
      description: 'Code to production time',
      target: 1.5,
      unit: 'days',
      status: 'excellent'
    },
    {
      id: 'error_rate',
      name: 'Error Rate',
      value: 0.02,
      change: -25.0,
      positive: true,
      description: 'System error percentage',
      target: 0.05,
      unit: '%',
      status: 'healthy'
    }
  ];

  // Developer experience metrics
  const developerMetrics = [
    {
      name: 'Developer Satisfaction',
      value: 8.2,
      max: 10,
      change: 0.5,
      positive: true
    },
    {
      name: 'Build Success Rate',
      value: 94,
      max: 100,
      change: 2.1,
      positive: true
    },
    {
      name: 'Code Review Time',
      value: 4.2,
      max: 8,
      change: -1.3,
      positive: true
    },
    {
      name: 'Technical Debt Ratio',
      value: 18,
      max: 30,
      change: -3.2,
      positive: true
    }
  ];

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'healthy': return 'text-green-400 bg-green-900 border-green-600';
      case 'warning': return 'text-yellow-400 bg-yellow-900 border-yellow-600';
      case 'critical': return 'text-red-400 bg-red-900 border-red-600';
      case 'excellent': return 'text-blue-400 bg-blue-900 border-blue-600';
      default: return 'text-gray-400 bg-gray-900 border-gray-600';
    }
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'healthy': return <CheckCircle className="w-4 h-4" />;
      case 'warning': return <AlertTriangle className="w-4 h-4" />;
      case 'critical': return <AlertTriangle className="w-4 h-4" />;
      case 'excellent': return <Target className="w-4 h-4" />;
      default: return <Clock className="w-4 h-4" />;
    }
  };

  const getServiceStatusColor = (status: string) => {
    switch (status) {
      case 'healthy': return 'bg-green-500';
      case 'warning': return 'bg-yellow-500';
      case 'critical': return 'bg-red-500';
      default: return 'bg-gray-500';
    }
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between">
        <div>
          <h1 className="text-3xl font-bold text-white mb-2 flex items-center space-x-3">
            <Zap className="w-8 h-8 text-purple-400" />
            <span>Technology Excellence Dashboard</span>
          </h1>
          <p className="text-gray-400">System health, development velocity, and technical metrics</p>
        </div>
        <div className="flex items-center space-x-3 mt-4 lg:mt-0">
          <select
            value={selectedTimeframe}
            onChange={(e) => setSelectedTimeframe(e.target.value)}
            className="bg-gray-800 text-white px-4 py-2 rounded-lg border border-gray-700 focus:border-indigo-500 focus:outline-none"
          >
            <option value="1h">Last hour</option>
            <option value="24h">Last 24 hours</option>
            <option value="7d">Last 7 days</option>
            <option value="30d">Last 30 days</option>
          </select>
          <div className="flex items-center space-x-2">
            <div className="w-3 h-3 bg-green-500 rounded-full animate-pulse"></div>
            <span className="text-sm text-gray-300">Real-time Monitoring</span>
          </div>
        </div>
      </div>

      {/* Key Metrics */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
        {techMetrics.map((metric) => (
          <div key={metric.id} className="bg-gray-800 rounded-lg p-6 border border-gray-700">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-sm font-medium text-gray-300">{metric.name}</h3>
              <div className={`flex items-center space-x-1 px-2 py-1 rounded-full text-xs border ${getStatusColor(metric.status)}`}>
                {getStatusIcon(metric.status)}
                <span className="capitalize">{metric.status}</span>
              </div>
            </div>
            <div className="flex items-baseline space-x-2 mb-2">
              <span className="text-2xl font-bold text-white">{metric.value}</span>
              <span className="text-sm text-gray-400">{metric.unit}</span>
            </div>
            <div className="flex items-center justify-between">
              <span className={`text-sm ${metric.positive ? 'text-green-500' : 'text-red-500'}`}>
                {metric.positive ? '+' : ''}{metric.change}%
              </span>
              <span className="text-xs text-gray-500">Target: {metric.target}{metric.unit}</span>
            </div>
          </div>
        ))}
      </div>

      {/* System Health Overview */}
      <div className="bg-gray-800 rounded-lg p-6 border border-gray-700">
        <h3 className="text-lg font-semibold text-white mb-4 flex items-center space-x-2">
          <Activity className="w-5 h-5 text-green-400" />
          <span>System Health Overview</span>
        </h3>
        <ResponsiveContainer width="100%" height={300}>
          <AreaChart data={systemHealthData}>
            <CartesianGrid strokeDasharray="3 3" stroke="#374151" />
            <XAxis dataKey="time" stroke="#9CA3AF" />
            <YAxis stroke="#9CA3AF" />
            <Tooltip
              contentStyle={{
                backgroundColor: '#1F2937',
                border: '1px solid #374151',
                borderRadius: '8px',
                color: '#F9FAFB'
              }}
            />
            <Area type="monotone" dataKey="uptime" stackId="1" stroke="#10B981" fill="#10B981" fillOpacity={0.6} />
            <Area type="monotone" dataKey="response" stackId="2" stroke="#3B82F6" fill="#3B82F6" fillOpacity={0.4} />
          </AreaChart>
        </ResponsiveContainer>
      </div>

      {/* Charts Section */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Deployment Velocity */}
        <div className="bg-gray-800 rounded-lg p-6 border border-gray-700">
          <h3 className="text-lg font-semibold text-white mb-4 flex items-center space-x-2">
            <GitBranch className="w-5 h-5 text-blue-400" />
            <span>Deployment Velocity</span>
          </h3>
          <ResponsiveContainer width="100%" height={300}>
            <BarChart data={deploymentData}>
              <CartesianGrid strokeDasharray="3 3" stroke="#374151" />
              <XAxis dataKey="week" stroke="#9CA3AF" />
              <YAxis stroke="#9CA3AF" />
              <Tooltip
                contentStyle={{
                  backgroundColor: '#1F2937',
                  border: '1px solid #374151',
                  borderRadius: '8px',
                  color: '#F9FAFB'
                }}
              />
              <Bar dataKey="deployments" fill="#3B82F6" name="Total Deployments" />
              <Bar dataKey="success" fill="#10B981" name="Successful" />
            </BarChart>
          </ResponsiveContainer>
        </div>

        {/* Technical Debt Distribution */}
        <div className="bg-gray-800 rounded-lg p-6 border border-gray-700">
          <h3 className="text-lg font-semibold text-white mb-4 flex items-center space-x-2">
            <Code className="w-5 h-5 text-orange-400" />
            <span>Technical Debt Distribution</span>
          </h3>
          <ResponsiveContainer width="100%" height={300}>
            <PieChart>
              <Pie
                data={technicalDebtData}
                cx="50%"
                cy="50%"
                outerRadius={100}
                dataKey="debt"
                label={({ category, debt }) => `${category}: ${debt}%`}
                labelLine={false}
              >
                {technicalDebtData.map((entry, index) => (
                  <Cell key={`cell-${index}`} fill={entry.color} />
                ))}
              </Pie>
              <Tooltip
                contentStyle={{
                  backgroundColor: '#1F2937',
                  border: '1px solid #374151',
                  borderRadius: '8px',
                  color: '#F9FAFB'
                }}
              />
            </PieChart>
          </ResponsiveContainer>
        </div>
      </div>

      {/* Service Health & Developer Experience */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Service Health Status */}
        <div className="bg-gray-800 rounded-lg p-6 border border-gray-700">
          <h3 className="text-lg font-semibold text-white mb-4 flex items-center space-x-2">
            <Server className="w-5 h-5 text-cyan-400" />
            <span>Service Health Status</span>
          </h3>
          <div className="space-y-4">
            {serviceHealthData.map((service, index) => (
              <div key={index} className="bg-gray-700 rounded-lg p-4">
                <div className="flex items-center justify-between mb-3">
                  <div className="flex items-center space-x-3">
                    <div className={`w-3 h-3 rounded-full ${getServiceStatusColor(service.status)}`}></div>
                    <h4 className="text-white font-medium">{service.service}</h4>
                  </div>
                  <span className="text-sm text-gray-400">{service.uptime}% uptime</span>
                </div>
                <div className="grid grid-cols-3 gap-4 text-sm">
                  <div>
                    <div className="text-gray-400">Response Time</div>
                    <div className="text-white font-medium">{service.response}ms</div>
                  </div>
                  <div>
                    <div className="text-gray-400">CPU Load</div>
                    <div className="text-white font-medium">{service.load}%</div>
                  </div>
                  <div>
                    <div className="text-gray-400">Status</div>
                    <div className={`font-medium capitalize ${
                      service.status === 'healthy' ? 'text-green-400' :
                      service.status === 'warning' ? 'text-yellow-400' : 'text-red-400'
                    }`}>
                      {service.status}
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Developer Experience */}
        <div className="bg-gray-800 rounded-lg p-6 border border-gray-700">
          <h3 className="text-lg font-semibold text-white mb-4 flex items-center space-x-2">
            <Users className="w-5 h-5 text-pink-400" />
            <span>Developer Experience</span>
          </h3>
          <div className="space-y-6">
            {developerMetrics.map((metric, index) => (
              <div key={index}>
                <div className="flex items-center justify-between mb-2">
                  <span className="text-sm text-gray-300">{metric.name}</span>
                  <div className="flex items-center space-x-2">
                    <span className="text-sm font-medium text-white">{metric.value}</span>
                    <span className={`text-xs ${metric.positive ? 'text-green-400' : 'text-red-400'}`}>
                      {metric.positive ? '+' : ''}{metric.change}
                    </span>
                  </div>
                </div>
                <div className="w-full bg-gray-600 rounded-full h-2">
                  <div 
                    className="bg-pink-500 h-2 rounded-full transition-all duration-300" 
                    style={{ width: `${(metric.value / metric.max) * 100}%` }}
                  ></div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Infrastructure Metrics */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
        <div className="bg-gray-800 rounded-lg p-6 border border-gray-700">
          <div className="flex items-center space-x-3 mb-4">
            <Cpu className="w-6 h-6 text-blue-400" />
            <h3 className="text-lg font-semibold text-white">CPU Usage</h3>
          </div>
          <div className="text-3xl font-bold text-white mb-2">68%</div>
          <div className="text-sm text-gray-400">Average across all nodes</div>
          <div className="mt-3 w-full bg-gray-700 rounded-full h-2">
            <div className="bg-blue-500 h-2 rounded-full" style={{ width: '68%' }}></div>
          </div>
        </div>

        <div className="bg-gray-800 rounded-lg p-6 border border-gray-700">
          <div className="flex items-center space-x-3 mb-4">
            <HardDrive className="w-6 h-6 text-green-400" />
            <h3 className="text-lg font-semibold text-white">Memory Usage</h3>
          </div>
          <div className="text-3xl font-bold text-white mb-2">72%</div>
          <div className="text-sm text-gray-400">8.6GB / 12GB used</div>
          <div className="mt-3 w-full bg-gray-700 rounded-full h-2">
            <div className="bg-green-500 h-2 rounded-full" style={{ width: '72%' }}></div>
          </div>
        </div>

        <div className="bg-gray-800 rounded-lg p-6 border border-gray-700">
          <div className="flex items-center space-x-3 mb-4">
            <Database className="w-6 h-6 text-purple-400" />
            <h3 className="text-lg font-semibold text-white">Database Load</h3>
          </div>
          <div className="text-3xl font-bold text-white mb-2">45%</div>
          <div className="text-sm text-gray-400">Query performance optimal</div>
          <div className="mt-3 w-full bg-gray-700 rounded-full h-2">
            <div className="bg-purple-500 h-2 rounded-full" style={{ width: '45%' }}></div>
          </div>
        </div>

        <div className="bg-gray-800 rounded-lg p-6 border border-gray-700">
          <div className="flex items-center space-x-3 mb-4">
            <Network className="w-6 h-6 text-yellow-400" />
            <h3 className="text-lg font-semibold text-white">Network I/O</h3>
          </div>
          <div className="text-3xl font-bold text-white mb-2">2.4GB/s</div>
          <div className="text-sm text-gray-400">Peak throughput</div>
          <div className="mt-3 w-full bg-gray-700 rounded-full h-2">
            <div className="bg-yellow-500 h-2 rounded-full" style={{ width: '60%' }}></div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default TechnologyDashboard;